'use client';
import React, { useState, useEffect, useRef } from 'react';
import { SUPPORTED_LANGUAGES } from '@/lib/i18n';
import { useLanguage } from '@/lib/LanguageContext';

const Navbar: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLabels: Record<string, Record<string, string>> = {
    es: {
      inicio: 'Inicio',
      nosotros: 'Nosotros',
      servicios: 'Servicios',
      noticias: 'Noticias',
      faq: 'FAQ',
      contactenos: 'Contacto',
      agendarReunion: 'Agendar Reunión',
    },
    fr: {
      inicio: 'Accueil',
      nosotros: 'À propos',
      servicios: 'Services',
      noticias: 'Actualités',
      faq: 'FAQ',
      contactenos: 'Contact',
      agendarReunion: 'Prendre RDV',
    },
  };

  const t = navLabels[language] ?? navLabels['es'];

  const navLinks = [
    { label: t.nosotros, href: '/about' },
    { label: t.servicios, href: '/#servicios' },
    { label: t.noticias, href: '/#noticias' },
    { label: t.faq, href: '/faq' },
  ];

  const handleLanguageToggle = () => {
    const next = language === 'es' ? 'fr' : 'es';
    setLanguage(next);
  };

  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === language);
  const otherLang = SUPPORTED_LANGUAGES.find((l) => l.code !== language);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-[#141618]/95 backdrop-blur-sm border-b border-white/5'
            : 'bg-transparent'
        }`}
        style={{ fontFamily: "'articulat-cf', sans-serif" }}>
        
        {/* Grid column lines */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 bottom-0 left-1/4 w-px bg-white/5" />
          <div className="absolute top-0 bottom-0 left-1/2 w-px bg-white/5" />
          <div className="absolute top-0 bottom-0 left-3/4 w-px bg-white/5" />
        </div>

        <div className="max-w-[1280px] mx-auto px-6 py-4 flex items-center justify-between relative z-10">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3 group flex-shrink-0">
            {/* Dot grid logo mark */}
            <div className="grid grid-cols-3 gap-[4px]">
              {[...Array(9)].map((_, i) => (
                <div
                  key={i}
                  className="w-[5px] h-[5px] rounded-full transition-all duration-300"
                  style={{
                    backgroundColor: i === 0 || i === 2 || i === 6 || i === 8 ? '#abd233' : 'rgba(255,255,255,0.4)',
                    transitionDelay: `${i * 30}ms`,
                  }}
                />
              ))}
            </div>
            <span
              className="text-white font-medium tracking-tight"
              style={{ fontSize: '18px', letterSpacing: '-0.01em' }}>
              INNOBOX
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link, i) => (
              <a
                key={i}
                href={link.href}
                className="cipher-nav-link">
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop Right */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={handleLanguageToggle}
              title={`Switch to ${otherLang?.label}`}
              aria-label={`Switch language to ${otherLang?.label}`}
              className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors duration-200"
              style={{ fontSize: '12px', letterSpacing: '0.05em' }}>
              <span className="text-base">{otherLang?.flag}</span>
              <span>{otherLang?.label}</span>
            </button>

            <a
              href="https://calendar.app.google/qrMMuz1WHJJ4eoab8"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center overflow-hidden"
              style={{
                border: '1px solid rgba(255,255,255,0.25)',
                padding: '9px 20px',
                fontSize: '13px',
                color: '#ffffff',
                backgroundColor: 'transparent',
                transition: 'all 300ms ease-out',
                letterSpacing: '0.02em',
              }}>
              <span
                className="absolute inset-0 bg-white transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"
                style={{ zIndex: 0 }}
              />
              <span className="relative z-10 transition-colors duration-300 group-hover:text-[#141618]">
                {t.agendarReunion}
              </span>
            </a>

            <a
              href="/contact"
              className="group relative inline-flex items-center overflow-hidden"
              style={{
                border: '1px solid #abd233',
                padding: '9px 20px',
                fontSize: '13px',
                color: '#abd233',
                backgroundColor: 'transparent',
                transition: 'all 300ms ease-out',
                letterSpacing: '0.02em',
              }}>
              <span
                className="absolute inset-0 bg-[#abd233] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"
                style={{ zIndex: 0 }}
              />
              <span className="relative z-10 transition-colors duration-300 group-hover:text-[#141618]">
                {t.contactenos}
              </span>
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden flex flex-col gap-[5px] cursor-pointer p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu">
            <span
              className={`block w-6 h-px bg-white transition-all duration-300 ${
                menuOpen ? 'rotate-45 translate-y-[6px]' : ''
              }`}
            />
            <span
              className={`block w-6 h-px bg-white transition-all duration-300 ${
                menuOpen ? 'opacity-0' : ''
              }`}
            />
            <span
              className={`block w-6 h-px bg-white transition-all duration-300 ${
                menuOpen ? '-rotate-45 -translate-y-[6px]' : ''
              }`}
            />
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        ref={menuRef}
        className={`fixed inset-0 z-40 bg-[#141618] transition-all duration-500 flex flex-col ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ paddingTop: '80px' }}>
        <div className="flex flex-col h-full px-6 py-8">
          <nav className="flex flex-col gap-1 flex-1">
            {[{ label: t.inicio, href: '/' }, ...navLinks].map((link, i) => (
              <a
                key={i}
                href={link.href}
                className="block text-white/70 hover:text-white py-4 border-b border-white/5 transition-colors"
                style={{ fontSize: '24px', letterSpacing: '-0.01em' }}
                onClick={() => setMenuOpen(false)}>
                {link.label}
              </a>
            ))}
          </nav>
          <div className="flex flex-col gap-3 pt-6">
            <a
              href="https://calendar.app.google/qrMMuz1WHJJ4eoab8"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center border border-white/20 text-white py-3 px-6 text-sm"
              onClick={() => setMenuOpen(false)}>
              {t.agendarReunion}
            </a>
            <a
              href="/contact"
              className="block text-center border border-[#abd233] text-[#abd233] py-3 px-6 text-sm"
              onClick={() => setMenuOpen(false)}>
              {t.contactenos}
            </a>
            <button
              onClick={handleLanguageToggle}
              className="flex items-center justify-center gap-2 text-white/50 hover:text-white transition-colors py-2"
              style={{ fontSize: '13px' }}>
              <span className="text-lg">{otherLang?.flag}</span>
              <span>Switch to {otherLang?.label}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;